<template>
  <div class="content-grid content-grid--subpage content-grid--subpage-table">
    <Breadcrumbs :title="t(`routes.admin.children.activities.name`)"/>
    <div class="content-grid__header">
      <h1 class="back-link-title">{{ t(`routes.admin.children.activities.name`) }}</h1>
      <div class="content-grid__filters">
        <SearchInput v-model="searchValue"/>
        <FormCheckbox v-model="showPastActivities"
                      :label="t('activities.showPastActivities')" class="form"
                      name="show-past-activities"/>
      </div>
    </div>
    <Card :title="t('global.quickLinks')" class="card--row-content">
      <BtnLink
          :name="t('routes.admin.children.activities.add.name')"
          :path="{name: `admin.children.activities.add`}"
      />
      <BtnLink
          :name="t(`routes.admin.children.activities.participants.add.name`)"
          :path="{name: `admin.children.activities.participants.add`}"
      />
      <BtnLink
          :name="t(`routes.admin.children.activities.registrations.add.name`)"
          :path="{name: `admin.children.activities.registrations.add`}"
      />
    </Card>
    <Card>
      <DataTable
          :headers="activityOccurrencesHeader"
          :is-loading="activityOccurrencesAreLoading"
          :items="activityOccurrenceItems"
          :search-value="searchValue"
          @delete="deleteActivityOccurrence"
          @duplicate="duplicateActivityOccurrence"
      />
    </Card>
  </div>
</template>

<script lang="ts" setup>
import {useRouter} from 'vue-router';
import Card from "@/components/layout/Card.vue";
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import SearchInput from "@/components/layout/SearchInput.vue";
import {useI18n} from "vue3-i18n";
import {computed, onMounted, ref, watch} from "vue";
import {useActivityOccurrenceService} from "@/inversify.config";
import DataTable from "@/components/layout/DataTable.vue";
import BtnLink from "@/components/layout/BtnLink.vue";
import {Guid} from "@/types";
import {notifyError, notifySuccess} from "@/notify";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";

const router = useRouter();
const {t} = useI18n()

const activityOccurrenceService = useActivityOccurrenceService();

const searchValue = ref("");
const showPastActivities = ref(false);
const activityOccurrencesAreLoading = ref(false);
const activityOccurrences = ref<ActivityOccurrence[]>([]);

watch(showPastActivities, async (newValue) => {
  await loadFutureOrPastOccurrences(newValue)
})

onMounted(async () => {
  await loadFutureOrPastOccurrences(showPastActivities.value);
})

const activityOccurrenceItems = computed(() => {
  let items: any[] = [];
  if (activityOccurrences.value.length === 0)
    return []

  activityOccurrences.value.forEach((activityOccurrence: ActivityOccurrence) => {
    const occupiedPlaces = activityOccurrence.occupiedPlaces != null ? activityOccurrence.occupiedPlaces : 0
    const name = activityOccurrence.activity?.nameFr;
    const truncatedName = activityOccurrence.activity?.nameFr?.truncateString(50);
    items.push({
      id: activityOccurrence.id,
      activity: activityOccurrence.activity,
      name: truncatedName,
      fullName: truncatedName == name ? undefined : name,
      startsAt: activityOccurrence.activity.startsAt,
      endsAt: activityOccurrence.activity.endsAt,
      startDate: activityOccurrence.startDate ? activityOccurrence.startDate : t("global.undefined"),
      participants: `${occupiedPlaces}/${activityOccurrence.activity.maxParticipants}`,
      isPublished: activityOccurrence.activity?.isPublished ? t("global.yes") : t("global.no"),
      actions: {
        update: {name: "admin.children.activities.update", params: {id: activityOccurrence.activity?.id}},
        participants: {
          name: "admin.children.activities.participants.edit",
          params: {activityId: activityOccurrence.activity?.id, activityOccurrenceId: activityOccurrence.id}
        },
        registrations: {
          name: "admin.children.activities.registrations.edit",
          params: {activityId: activityOccurrence.activity?.id}
          },
        duplicate: true,
        delete: true,
      }
    });
  });

  return items;
});

async function loadFutureOrPastOccurrences(showPastActivities: boolean) {
  activityOccurrencesAreLoading.value = true;
  let response = showPastActivities
      ? await activityOccurrenceService.getPastActivityOccurrences()
      : await activityOccurrenceService.getFutureActivityOccurrences();
  if (response.length > 0) {
    activityOccurrences.value = response;
  }
  activityOccurrencesAreLoading.value = false;
}

async function duplicateActivityOccurrence(activityOccurrence: any) {
  if (activityOccurrence?.id == null || !Guid.isValid(activityOccurrence.id ?? "")) {
    return;
  }

  try {
    const activityId = activityOccurrence.activity?.id;

    if (!activityId || !Guid.isValid(activityId)) {
      return;
    }

    await router.push({
      name: 'admin.children.activities.add',
      query: {
        duplicateFromId: activityId
      }
    });
  } catch (error) {
    notifyError(t('activityOccurrences.form.duplicateError'));
  }
}

async function deleteActivityOccurrence(activityOccurrence: any) {
  if (activityOccurrence?.id == null || !Guid.isValid(activityOccurrence.id ?? ""))
    return

  let confirmDelete = confirm(t("activityOccurrences.form.deleteConfirmation"));
  if (!confirmDelete)
    return;

  let result: SucceededOrNotResponse = await activityOccurrenceService.deleteActivityOccurrence(activityOccurrence.id);
  if (result?.succeeded) {
    notifySuccess(t('activityOccurrences.form.deleteSuccess'))
    await loadFutureOrPastOccurrences(showPastActivities.value);
  } else {
    notifyError(t('activityOccurrences.form.deleteError'))
  }
}

const activityOccurrencesHeader = [
  {
    text: t("products.form.name"),
    value: "name",
    sortable: true,
    width: 315,
    tooltip: "fullName"
  },
  {
    text: t("activities.form.startDateShort"),
    value: "startDate",
    sortable: true,
    width: 100,
  },
  {
    text: t("activities.form.startsAtShort"),
    value: "startsAt",
    sortable: true,
    width: 100,
  },
  {
    text: t("activities.form.endsAtShort"),
    value: "endsAt",
    sortable: true,
    width: 100,
  },
  {
    text: t("activities.form.fieldset-participants"),
    value: "participants",
    width: 80,
  },
  {
    text: t("activities.form.isPublished"),
    value: "isPublished",
    width: 80,
  },
  {
    text: t("global.table.actions"),
    value: "actions",
    width: 175
  },
];
</script>
